import React, { useState, useEffect, useCallback } from 'react';
import {
    FiX, FiShield, FiAlertTriangle, FiLock, FiUsers,
    FiRefreshCw, FiUser, FiActivity
} from 'react-icons/fi';
import { apiFetch } from '../utils/api';

const PLAN_COLORS = {
    free:       'bg-gray-700 text-gray-300',
    starter:    'bg-blue-900/50 text-blue-300',
    team:       'bg-purple-900/50 text-purple-300',
    business:   'bg-amber-900/50 text-amber-300',
    enterprise: 'bg-emerald-900/50 text-emerald-300',
};

const RoleBadge = ({ role }) => {
    const c = role === 'owner' ? 'bg-amber-900/50 text-amber-300' :
              role === 'admin' ? 'bg-blue-900/50 text-blue-300' :
              'bg-gray-700 text-gray-400';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c}`}>{role}</span>;
};

const ACTION_COLORS = {
    login:              '#10b981',
    logout:             '#6b7280',
    login_failed:       '#ef4444',
    account_locked:     '#dc2626',
    tenant_created:     '#3b82f6',
    message_created:    '#10b981',
    message_updated:    '#06b6d4',
    message_deleted:    '#f87171',
    member_joined:      '#34d399',
    member_removed:     '#fb923c',
    profile_updated:    '#60a5fa',
    password_changed:   '#f59e0b',
};

const TABS = [
    { id: 'profile',  label: 'Profil',         icon: FiUser },
    { id: 'orgs',     label: 'Organisations',   icon: FiUsers },
    { id: 'logs',     label: 'Activité récente',icon: FiActivity },
];

const UserDetailDrawer = ({ userId, onClose, onUpdate }) => {
    const [tab,           setTab]           = useState('profile');
    const [user,          setUser]          = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [notification,  setNotification]  = useState(null);

    const notify = (msg, ok = true) => {
        setNotification({ msg, ok });
        setTimeout(() => setNotification(null), 3000);
    };

    const loadUser = useCallback(async () => {
        setLoading(true);
        try {
            const d = await apiFetch(`/superadmin/users/${userId}`);
            setUser(d);
        } catch (e) { notify(e.message, false); }
        finally { setLoading(false); }
    }, [userId]);

    useEffect(() => { loadUser(); }, [loadUser]);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const handleResetAttempts = async () => {
        setActionLoading('reset');
        try {
            await apiFetch(`/superadmin/users/${userId}/reset-attempts`, { method: 'POST' });
            setUser(u => ({ ...u, loginAttempts: 0 }));
            notify('Tentatives réinitialisées');
            onUpdate?.();
        } catch (e) { notify(e.message, false); }
        finally { setActionLoading(null); }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/60 z-[999]" onClick={onClose} />

            <div className="fixed right-0 top-0 h-full w-full sm:w-[600px] bg-gray-950 border-l border-gray-800 flex flex-col shadow-2xl z-[9999] animate-slideIn overflow-hidden">
                {notification && (
                    <div className={`absolute top-4 right-4 z-10 px-4 py-2 rounded-lg text-sm font-medium ${
                        notification.ok ? 'bg-emerald-900/90 text-emerald-200' : 'bg-red-900/90 text-red-200'
                    }`}>
                        {notification.msg}
                    </div>
                )}

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 shrink-0">
                    {user ? (
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="relative w-10 h-10 rounded-full bg-brand-600/20 flex items-center justify-center text-brand-400 font-bold text-sm shrink-0">
                                {(user.name || '?')[0].toUpperCase()}
                                {user.twoFactorEnabled && (
                                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-gray-950 flex items-center justify-center">
                                        <FiLock size={8} className="text-white" />
                                    </div>
                                )}
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="text-white font-bold text-lg">{user.name}</h2>
                                    {user.isSuperAdmin && (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-brand-900/50 text-brand-300 flex items-center gap-1">
                                            <FiShield size={10} /> Super Admin
                                        </span>
                                    )}
                                    {!user.isEmailVerified && <FiAlertTriangle size={13} className="text-amber-400" />}
                                </div>
                                <p className="text-gray-500 text-sm">{user.email}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="h-6 w-48 bg-gray-800 rounded animate-pulse" />
                    )}
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-gray-500 hover:text-white transition-colors shrink-0">
                        <FiX size={18} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-800 px-6 shrink-0">
                    {TABS.map(t => {
                        const Icon = t.icon;
                        return (
                            <button key={t.id} onClick={() => setTab(t.id)}
                                className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    tab === t.id
                                        ? 'border-brand-500 text-white'
                                        : 'border-transparent text-gray-500 hover:text-gray-300'
                                }`}>
                                <Icon size={13} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {loading ? (
                        <div className="flex justify-center py-16">
                            <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : user && (
                        <>
                            {/* ── PROFIL ── */}
                            {tab === 'profile' && (
                                <div className="space-y-5">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {[
                                            ['ID', user._id],
                                            ['Auth', user.authProvider],
                                            ['Thème', user.theme || '—'],
                                            ['Email vérifié', user.isEmailVerified ? 'Oui' : 'Non'],
                                            ['2FA', user.twoFactorEnabled ? 'Activé' : 'Désactivé'],
                                            ['Notifs email', user.emailNotifications ? 'Oui' : 'Non'],
                                            ['Inscrit le', new Date(user.createdAt).toLocaleDateString('fr-FR')],
                                            ['Orgs', user.tenants?.length ?? 0],
                                            ['Tentatives login', user.loginAttempts ?? 0],
                                        ].map(([k, v]) => (
                                            <div key={k} className={`bg-gray-900 rounded-lg p-3 ${k === 'Tentatives login' && (user.loginAttempts ?? 0) >= 3 ? 'border border-amber-800/50' : ''}`}>
                                                <div className="text-gray-500 text-xs mb-1">{k}</div>
                                                <div className={`font-mono text-xs break-all ${k === 'Tentatives login' && (user.loginAttempts ?? 0) >= 3 ? 'text-amber-400' : 'text-white'}`}>
                                                    {String(v)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Alerte brute force */}
                                    {(user.loginAttempts ?? 0) >= 3 && (
                                        <div className="flex items-center justify-between gap-3 bg-amber-900/20 border border-amber-800/50 rounded-xl px-4 py-3">
                                            <div className="flex items-center gap-2 text-amber-300 text-sm">
                                                <FiAlertTriangle size={14} />
                                                {(user.loginAttempts ?? 0) >= 5
                                                    ? `Compte verrouillé (${user.loginAttempts} tentatives)`
                                                    : `${user.loginAttempts} tentatives de connexion échouées`
                                                }
                                            </div>
                                            <button onClick={handleResetAttempts} disabled={actionLoading === 'reset'}
                                                className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-900/40 border border-amber-700 text-amber-300 hover:bg-amber-900/60 transition-colors disabled:opacity-50">
                                                <FiRefreshCw size={12} className={actionLoading === 'reset' ? 'animate-spin' : ''} />
                                                Réinitialiser
                                            </button>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="bg-gray-900 rounded-xl p-4">
                                        <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Actions</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {(user.loginAttempts ?? 0) === 0 ? (
                                                <span className="text-gray-600 text-xs">Aucune tentative en cours</span>
                                            ) : (
                                                <button onClick={handleResetAttempts} disabled={actionLoading === 'reset'}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors disabled:opacity-50">
                                                    <FiRefreshCw size={12} className={actionLoading === 'reset' ? 'animate-spin' : ''} />
                                                    Reset tentatives login
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── ORGANISATIONS ── */}
                            {tab === 'orgs' && (
                                <div className="space-y-3">
                                    <p className="text-gray-500 text-sm">{user.tenants?.length ?? 0} organisation{(user.tenants?.length ?? 0) > 1 ? 's' : ''}</p>
                                    {user.tenants?.length === 0 ? (
                                        <p className="text-gray-600 text-sm">Aucune organisation</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {user.tenants.map((t, i) => (
                                                <div key={i} className="bg-gray-900 rounded-xl p-4">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                                <span className="text-white font-semibold text-sm">
                                                                    {t.tenant?.name || t.tenantId}
                                                                </span>
                                                                {t.tenant?.status === 'suspended' && (
                                                                    <span className="text-xs text-red-400">(suspendu)</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                {t.tenant?.plan && (
                                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PLAN_COLORS[t.tenant.plan] || 'bg-gray-700 text-gray-300'}`}>
                                                                        {t.tenant.plan}
                                                                    </span>
                                                                )}
                                                                <RoleBadge role={t.role} />
                                                            </div>
                                                        </div>
                                                        <span className="text-gray-600 text-xs shrink-0">
                                                            {t.joinedAt ? new Date(t.joinedAt).toLocaleDateString('fr-FR') : ''}
                                                        </span>
                                                    </div>
                                                    {t.tenant && (
                                                        <div className="mt-2 flex gap-4 text-xs text-gray-600">
                                                            <span>/{t.tenant.slug}</span>
                                                            <span>{t.tenant.type}</span>
                                                            <span className="font-mono text-gray-700">{String(t.tenantId)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── ACTIVITÉ ── */}
                            {tab === 'logs' && (
                                <div className="space-y-2">
                                    <p className="text-gray-500 text-sm">{user.recentLogs?.length ?? 0} derniers événements</p>
                                    {user.recentLogs?.length === 0 ? (
                                        <p className="text-gray-600 text-sm">Aucune activité</p>
                                    ) : (
                                        <div className="space-y-1.5">
                                            {user.recentLogs.map(log => {
                                                const color = ACTION_COLORS[log.action] || '#9ca3af';
                                                return (
                                                    <div key={log._id} className="bg-gray-900 rounded-xl px-4 py-3">
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
                                                            <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold"
                                                                style={{ background: color + '20', color }}>
                                                                {log.action}
                                                            </span>
                                                            {log.tenantName && (
                                                                <span className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded-lg">{log.tenantName}</span>
                                                            )}
                                                            <span className="text-gray-600 text-xs ml-auto">
                                                                {new Date(log.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                        {(log.ip || log.targetName) && (
                                                            <div className="mt-1 flex gap-3 text-xs text-gray-600 pl-4">
                                                                {log.targetName && <span>Cible : {log.targetName}</span>}
                                                                {log.ip && <span>IP : {log.ip}</span>}
                                                            </div>
                                                        )}
                                                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                                                            <pre className="mt-1 pl-4 text-xs font-mono text-gray-500 whitespace-pre-wrap overflow-auto max-h-20">
                                                                {JSON.stringify(log.metadata, null, 2)}
                                                            </pre>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserDetailDrawer;
